import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { TABLE_IDS, VisitPurposeDetails } from "../../../constants/common";
import { ArrowUpDown, Trash } from "lucide-react";
import { t } from "i18next";
import TableWrapper from "../TableWrapper";
import DeleteVisitPurposeModal from "../../modal/DeleteVisitPurposeModal";

interface VisitPurposeTableProps {
  visitPurposes: VisitPurposeDetails[];
  setSelectedPurposeId: Dispatch<SetStateAction<number>>;
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const VisitPurposeTable = ({
  visitPurposes,
  setSelectedPurposeId,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  pageSize,
  setPageSize,
}: VisitPurposeTableProps) => {
  const [isDeleteVisitPurposeModalOpen, setIsDeleteVisitPurposeModalOpen] =
    useState(false);
  const [selectedPurposeDetails, setSelectedPurposeDetails] = useState({
    purposeId: 0,
    locationId: 0,
  });

  const handleDeleteDetailCode = useCallback(
    async (purposeId: number, locationId: number) => {
      setIsDeleteVisitPurposeModalOpen(true);
      setSelectedPurposeDetails({
        purposeId,
        locationId,
      });
    },
    []
  );

  const visitPurposesColumns: ColumnDef<VisitPurposeDetails>[] = useMemo(
    () => [
      {
        accessorKey: "n_PurposeID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("code")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span
              onClick={() => setSelectedPurposeId(row.original.n_PurposeID)}
              className="text-blue-800 underline cursor-pointer capitalize"
            >
              {row.original.n_PurposeID}
            </span>
          );
        },
      },
      {
        accessorKey: "s_PurposeEn",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("purposeEnglish")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_PurposeEn}</span>;
        },
      },
      {
        accessorKey: "s_PurposeAr",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            type="button"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("purposeArabic")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_PurposeAr}</span>;
        },
      },
      // {
      //   accessorKey: "s_VisitRequestTypeEn",
      //   header: ({ column }) => (
      //     <Button
      // //       className="flex w-full justify-between text-md"
      //       type="button"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("visitorType")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      //   cell: ({ row }) => {
      //     return (
      //       <span>
      //         {i18n.language === "en"
      //           ? row.original.s_VisitRequestTypeEn
      //           : row.original.s_VisitRequestTypeAr}
      //       </span>
      //     );
      //   },
      // },
      {
        header: t("action"),
        cell: ({ row }) => {
          return (
            <Trash
              onClick={() =>
                handleDeleteDetailCode(
                  row.original.n_PurposeID,
                  row.original.n_LocationID
                )
              }
              className="w-5 h-5 text-red-600 cursor-pointer"
            />
          );
        },
      },
    ],
    [handleDeleteDetailCode, setSelectedPurposeId]
  );

  return (
    <>
      <TableWrapper
        data={visitPurposes}
        columns={visitPurposesColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isSearch
        tableId={TABLE_IDS.VISIT_PURPOSE}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <DeleteVisitPurposeModal
        purposeDetails={selectedPurposeDetails}
        isOpen={isDeleteVisitPurposeModalOpen}
        onOpenChange={() => setIsDeleteVisitPurposeModalOpen(false)}
      />
    </>
  );
};

export default React.memo(VisitPurposeTable);
